import type { BusinessUnit } from '@backend/lib-l10n';

import { getJobsBusinessUnitUrlPath, getJobsUrl } from '~/config';
import { businessUnits, type Page } from '~/config/businessUnits';
import { getHubPageHref } from '~/utils/hubPage';
import { isMissionApplyUrl } from '~/utils/missionUrl';

/**
 * The path below the business unit segment, matched in any case, in the case it is served in: the slugs of list and
 * offer pages lowercase, a search URL keeps the city as typed (it is not a slug). `null` when the path is not under
 * the business unit.
 */
const findServedPagePath = (path: string, businessUnit: BusinessUnit) => {
  const jobsBusinessUnitUrlPath = getJobsBusinessUnitUrlPath(businessUnit);
  const lowerCasePath = path.toLowerCase();

  if (lowerCasePath !== jobsBusinessUnitUrlPath && !lowerCasePath.startsWith(`${jobsBusinessUnitUrlPath}/`)) {
    return null;
  }

  const pagePath = path.slice(jobsBusinessUnitUrlPath.length);
  const isSlugPath = Object.values(businessUnits[businessUnit].paths).includes(pagePath.split('/')[1]?.toLowerCase());

  return isSlugPath ? pagePath.toLowerCase() : pagePath;
};

// the folders of src/pages/[businessUnit]: a page answers on its folder's name too, not only on its localized path
const PAGE_BY_FOLDER: Record<string, Page> = {
  cities: 'cities',
  jobs: 'jobs',
  sectors: 'sectors',
  'job-offer': 'jobOffer',
  'pain-points': 'painPoints',
  solutions: 'solutions',
  sitemap: 'sitemap',
};

/** The path with its localized page segment: `/fr/jobs/cariste` is `/fr/metiers/cariste`. */
export const localizePagePath = (path: string, businessUnit: BusinessUnit) => {
  const [root, businessUnitSegment, folder, ...rest] = path.split('/');
  const page = Object.hasOwn(PAGE_BY_FOLDER, folder?.toLowerCase()) ? PAGE_BY_FOLDER[folder.toLowerCase()] : undefined;
  const localizedFolder = page && businessUnits[businessUnit].paths[page];
  return localizedFolder && localizedFolder !== folder.toLowerCase()
    ? [root, businessUnitSegment, localizedFolder, ...rest].join('/')
    : path;
};

/** The path in the case it is served in, see `findServedPagePath`. Any path outside the business unit is returned as is. */
export const normalizePathCase = (path: string, businessUnit: BusinessUnit) => {
  const pagePath = findServedPagePath(path, businessUnit);
  return pagePath === null ? path : `${getJobsBusinessUnitUrlPath(businessUnit)}${pagePath}`;
};

/** `page`: the page of offers a city, job or sector page lists, each page of its own as each lists other offers. */
export const getCanonicalUrl = (pathname: string, businessUnit: BusinessUnit, page = 1): string => {
  const { paths } = businessUnits[businessUnit];

  const cleanPath = pathname.split('?')[0];
  const pagePath = findServedPagePath(cleanPath, businessUnit) ?? cleanPath;
  const normalizedPath = `${getJobsBusinessUnitUrlPath(businessUnit)}${pagePath === '/' ? '' : pagePath}`;

  // the apply URL opens the application form over the job offer page: same content, one URL to index
  const applySuffix = `/${paths.apply}`;
  const isApplyPath = normalizedPath.includes(`/${paths.jobOffer}/`) && isMissionApplyUrl(businessUnit, normalizedPath);

  if (isApplyPath) {
    return `${getJobsUrl()}${normalizedPath.slice(0, -applySuffix.length)}`;
  }

  return `${getJobsUrl()}${getHubPageHref(normalizedPath, page)}`;
};
